import React, { useState } from 'react';
import { X, Loader2, CalendarPlus } from 'lucide-react';
import { CustomSelect } from '../common/CustomSelect';
import { CustomTimePicker } from '../common/CustomTimePicker';
import api from '../../api/auth.api';
import { API_CONFIG } from '../../config/api.config';

// "Add a missed day" request — for when a worker forgot to clock in entirely and
// no entry exists for that day. Submits a `missed_day` correction; on admin
// approval the backend INSERTs a new TIME_ENTRIES row. Request only, not an edit.

interface JobOption { job_id: number; job_name: string; bradanna_job_number?: string }

interface Props {
  weekStartDate: string;                              // Friday 'YYYY-MM-DD'
  missingDays: Array<{ value: string; label: string }>; // week days with no entry, not future
  jobs: JobOption[];
  language: 'en' | 'es';
  // When present, edit an existing PENDING missed-day request: prefill + on save
  // withdraw the old one first (replace), or withdraw alone.
  editing?: { correctionId: number; date: string; dateLabel: string; jobId: number | null; clockInIso?: string | null; clockOutIso?: string | null; note?: string | null } | null;
  onClose: () => void;
  onSubmitted: () => void;
}

const isoToHHMM = (iso?: string | null) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

const t = {
  en: {
    title: 'Add a Missed Day', editTitle: 'Edit Missed Day', subtitle: 'Forgot to clock in? Ask your manager to add this day.',
    date: 'Which day?', job: 'Job', clockIn: 'Clock In', clockOut: 'Clock Out',
    selectDate: 'Select a day', selectJob: 'Select a job',
    note: 'Add a note (optional)', notePlaceholder: 'Explain what happened…',
    submit: 'Send Request', save: 'Save Changes', withdraw: 'Withdraw request', cancel: 'Cancel',
    needDate: 'Pick the day you worked.', needJob: 'Pick the job.',
    needTimes: 'Enter clock-in and clock-out times.',
    noDays: 'Every day this week already has time. Nothing to add.',
    failed: 'Failed to send request. Please try again.',
  },
  es: {
    title: 'Agregar Día Faltante', editTitle: 'Editar Día Faltante', subtitle: '¿Olvidó marcar entrada? Pida a su gerente que agregue este día.',
    date: '¿Qué día?', job: 'Trabajo', clockIn: 'Entrada', clockOut: 'Salida',
    selectDate: 'Seleccione un día', selectJob: 'Seleccione un trabajo',
    note: 'Agregar una nota (opcional)', notePlaceholder: 'Explique qué pasó…',
    submit: 'Enviar Solicitud', save: 'Guardar Cambios', withdraw: 'Retirar solicitud', cancel: 'Cancelar',
    needDate: 'Elija el día que trabajó.', needJob: 'Elija el trabajo.',
    needTimes: 'Ingrese horas de entrada y salida.',
    noDays: 'Todos los días de esta semana ya tienen tiempo. Nada que agregar.',
    failed: 'No se pudo enviar la solicitud. Intente de nuevo.',
  },
};

export const AddMissedDayModal: React.FC<Props> = ({
  weekStartDate, missingDays, jobs, language, editing, onClose, onSubmitted,
}) => {
  const L = t[language];
  const isEdit = !!editing;
  const [date, setDate] = useState(editing?.date || '');
  const [jobId, setJobId] = useState<number | null>(editing?.jobId ?? null);
  const [clockIn, setClockIn] = useState(editing?.clockInIso ? isoToHHMM(editing.clockInIso) : '07:00');
  const [clockOut, setClockOut] = useState(editing?.clockOutIso ? isoToHHMM(editing.clockOutIso) : '16:00');
  const [note, setNote] = useState(editing?.note || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const withdraw = async () => {
    if (!editing) return;
    setBusy(true);
    setError(null);
    try {
      await api.post(API_CONFIG.ENDPOINTS.TIMECARD_REVIEW_WITHDRAW, { correction_ids: [editing.correctionId] });
      onSubmitted();
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || L.failed);
    } finally {
      setBusy(false);
    }
  };

  const submit = async () => {
    if (!date) { setError(L.needDate); return; }
    if (!jobId) { setError(L.needJob); return; }
    if (!clockIn || !clockOut) { setError(L.needTimes); return; }

    setBusy(true);
    setError(null);
    try {
      // Editing replaces the old pending request.
      if (editing) await api.post(API_CONFIG.ENDPOINTS.TIMECARD_REVIEW_WITHDRAW, { correction_ids: [editing.correctionId] });
      const res = await api.post(API_CONFIG.ENDPOINTS.TIMECARD_REVIEW_CORRECTIONS, {
        week_start_date: weekStartDate,
        corrections: [{
          entry_id: null,
          correction_type: 'missed_day',
          proposed_clock_in: clockIn,
          proposed_clock_out: clockOut,
          proposed_job_id: jobId,
          proposed_date: date,
          employee_notes: note,
        }],
        timezone_offset_minutes: new Date().getTimezoneOffset(),
      });
      if (!res.data?.success) throw new Error(res.data?.error || L.failed);
      onSubmitted();
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || L.failed);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={overlay} onClick={onClose}>
      <div style={sheet} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '20px 20px 14px' }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 22, fontWeight: 800, color: '#1f2937', display: 'flex', alignItems: 'center', gap: 8 }}>
              <CalendarPlus size={24} color="#4f46e5" /> {isEdit ? L.editTitle : L.title}
            </div>
            <div style={{ fontSize: 16, color: '#64748b', marginTop: 6 }}>{L.subtitle}</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 6, color: '#94a3b8' }}>
            <X size={26} />
          </button>
        </div>

        <div style={{ padding: '0 20px 20px', overflowY: 'auto' }}>
          {(!isEdit && missingDays.length === 0) ? (
            <div style={{ fontSize: 16, color: '#92400e', background: '#fffbeb', borderRadius: 12, padding: 16 }}>{L.noDays}</div>
          ) : (
            <>
              <label style={lbl}>{L.date}</label>
              <div style={{ marginBottom: 18 }}>
                {isEdit ? (
                  <div style={{ padding: 14, borderRadius: 10, border: '1px solid #e5e7eb', background: '#f8fafc', fontSize: 17, fontWeight: 700, color: '#1f2937' }}>{editing!.dateLabel}</div>
                ) : (
                  <CustomSelect value={date} onChange={(v) => setDate(String(v))} placeholder={L.selectDate} options={missingDays} />
                )}
              </div>

              <label style={lbl}>{L.job}</label>
              <div style={{ marginBottom: 18 }}>
                <CustomSelect
                  value={jobId || ''}
                  onChange={(v) => setJobId(Number(v))}
                  placeholder={L.selectJob}
                  options={jobs.map((j) => ({ value: j.job_id, label: j.bradanna_job_number ? `${j.job_name} (${j.bradanna_job_number})` : j.job_name }))}
                />
              </div>

              <div style={{ marginBottom: 18 }}>
                <CustomTimePicker value={clockIn} onChange={setClockIn} label={L.clockIn} />
              </div>
              <div style={{ marginBottom: 18 }}>
                <CustomTimePicker value={clockOut} onChange={setClockOut} label={L.clockOut} />
              </div>

              <label style={lbl}>{L.note}</label>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder={L.notePlaceholder}
                style={{ width: '100%', minHeight: 80, padding: 14, fontSize: 17, border: '1px solid #d1d5db', borderRadius: 10, boxSizing: 'border-box', resize: 'vertical' }}
              />
            </>
          )}

          {error && (
            <div style={{ marginTop: 14, background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', borderRadius: 10, padding: 12, fontSize: 15 }}>{error}</div>
          )}
        </div>

        <div style={{ padding: 18, borderTop: '1px solid #f1f5f9' }}>
          <div style={{ display: 'flex', gap: 12 }}>
            <button onClick={onClose} disabled={busy} style={{ flex: 1, padding: 18, borderRadius: 12, border: '1px solid #e5e7eb', background: '#fff', color: '#475569', fontSize: 18, fontWeight: 700, cursor: 'pointer' }}>
              {L.cancel}
            </button>
            <button onClick={submit} disabled={busy || (!isEdit && missingDays.length === 0)} style={{ flex: 2, padding: 18, borderRadius: 12, border: 'none', background: '#4f46e5', color: '#fff', fontSize: 18, fontWeight: 800, cursor: (busy || (!isEdit && missingDays.length === 0)) ? 'default' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, opacity: (busy || (!isEdit && missingDays.length === 0)) ? 0.6 : 1 }}>
              {busy && <Loader2 size={18} className="spin" />} {isEdit ? L.save : L.submit}
            </button>
          </div>
          {isEdit && (
            <button onClick={withdraw} disabled={busy} style={{ width: '100%', marginTop: 10, padding: 14, borderRadius: 12, border: 'none', background: 'none', color: '#dc2626', fontSize: 16, fontWeight: 700, cursor: busy ? 'default' : 'pointer' }}>
              {L.withdraw}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const lbl: React.CSSProperties = { fontSize: 16, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 8 };
const overlay: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1000,
  display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
};
const sheet: React.CSSProperties = {
  background: '#fff', width: '100%', maxWidth: 480, maxHeight: '92vh',
  borderTopLeftRadius: 22, borderTopRightRadius: 22, display: 'flex', flexDirection: 'column',
  boxShadow: '0 -4px 24px rgba(0,0,0,0.2)',
};

export default AddMissedDayModal;
